import { useState, FormEvent } from 'react';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

const businessTypes = [
  'Contractor / Construction',
  'Plumbing / HVAC',
  'Electrical',
  'Landscaping / Lawn Care',
  'Cleaning Services',
  'Auto Repair',
  'Legal / Law Firm',
  'Medical / Dental',
  'Real Estate',
  'Restaurant / Food Service',
  'Retail',
  'Other',
];

const FormSection = () => {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;
    setStatus('submitting');
    const formData = new FormData(form);
    const data = new URLSearchParams(formData as unknown as Record<string, string>);
    const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbw2f_DfEsshzogCIZdjjeDSm5rEavUx0Pf7Y7-419JxTPm7HsTd6HL-1j6cR34ki8WpwQ/exec';
    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      // no-cors means we can't read the response, so we assume success
      setStatus('success');
      form.reset();
      setCharCount(0);
      // Fire Google Ads conversion event
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', { send_to: 'AW-18051957491/XVcOCMPtj5kcEPOF7J9D' });
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="form" className="bg-white py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Copy */}
          <div>
            <span className="text-orange-500 text-xs font-bold uppercase tracking-widest">Free Strategy Call</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-3 mb-5 leading-tight">
              Let's Talk About Growing Your Business
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-8">
              Fill out the form and we'll schedule a direct, no-fluff strategy call. We'll focus on your business specifically — no generic advice, no sales pressure.
            </p>
            <ul className="space-y-4">
              {[
                'Understand how customers are currently finding (or missing) your business',
                'Identify exactly where you\'re losing potential leads',
                'Walk through a simple system for consistent inbound customers',
                'Get honest answers to any questions about getting started',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-orange-100 shrink-0 mt-0.5">
                    <i className="ri-check-line text-orange-500 text-xs" />
                  </div>
                  <span className="text-gray-600 text-sm">{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 p-6 bg-[#f8f9fa] rounded-2xl border border-gray-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-100">
                  <i className="ri-shield-check-line text-orange-500 text-lg" />
                </div>
                <span className="text-gray-900 font-semibold text-sm">100% Free. Zero Obligation.</span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">
                This is a real conversation about your business — nothing more. We'll never spam you or sell your information.
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-[#f8f9fa] rounded-2xl p-8 md:p-10 border border-gray-100">
            {status === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-5">
                  <i className="ri-check-double-line text-green-500 text-3xl" />
                </div>
                <h3 className="text-gray-900 font-bold text-2xl mb-3">You're All Set!</h3>
                <p className="text-gray-500 text-base">
                  We've received your request and will reach out within 1 business day to schedule your strategy call.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-gray-900 font-bold text-2xl mb-6">Book Your Strategy Call</h3>
                <form
                  data-readdy-form
                  id="strategy-call-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="John Smith"
                        className="w-full h-12 px-4 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Business Name *
                      </label>
                      <input
                        type="text"
                        name="business_name"
                        required
                        placeholder="Smith Plumbing Co."
                        className="w-full h-12 px-4 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="(555) 000-0000"
                        className="w-full h-12 px-4 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="john@example.com"
                        className="w-full h-12 px-4 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Type of Business *
                    </label>
                    <select
                      name="business_type"
                      required
                      className="w-full h-12 px-4 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all cursor-pointer"
                    >
                      <option value="">Select your industry...</option>
                      {businessTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Anything You Want Us to Know? (Optional)
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us a bit about your business and what you\'re trying to achieve..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all resize-none"
                    />
                    <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-orange-500' : 'text-gray-400'}`}>
                      {charCount}/500
                    </p>
                  </div>

                  {status === 'error' && (
                    <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="whitespace-nowrap cursor-pointer w-full h-14 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold text-base rounded-xl transition-all"
                  >
                    {status === 'submitting' ? 'Sending...' : 'Book a Strategy Call'}
                  </button>

                  <p className="text-center text-gray-400 text-xs">
                    No contracts. No spam. We'll respond within 1 business day.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FormSection;
